import { Prop, Vue, Watch } from 'vue-property-decorator';
import { ProductPropertyDto, ProductStatusDto, ShoppingCartItemDto } from '@/api';
import { ProductTuple } from '@/stores/search/util';
import { PriceService } from '@/util/PriceService';
import Big from 'big.js';
import { PriceFormatter } from '@/util/PriceFormatter';

export class AbstractProductWidget extends Vue {
  @Prop({required: true})
  public product!: ProductTuple;

  @Prop({required: false, default: false})
  public readOnly!: boolean;

  public localItem = this.createEmptyItem();

  private readonly properties = ProductPropertyDto;

  private createEmptyItem(): ShoppingCartItemDto {
    return {
      shopCode: 'GRM',
      sku: this.product.product.sku,
      amount: 1,
      unitAbbreviation: this.product.unit.abbreviation,
      unit: this.product.unit,
      product: this.product.product,
    };
  }

  get isReadOnly() {
    return this.readOnly;
  }

  get hasOfferPrice(): boolean {
    return !!this.product.product.offerPrice;
  }

  get effectiveProductPrice(): Big | null {
    return PriceService.findEffectivePrice(this.product.product);
  }

  get scaledEffectivePrice(): Big | null {
    return PriceService.findUnitPrice(this.product.unit, this.effectiveProductPrice);
  }

  get scaledEffectivePriceString(): string {
    return PriceFormatter.formatPrice(this.scaledEffectivePrice);
  }

  get listPrice(): Big | null {
    return PriceService.findListPrice(this.product.product);
  }

  get scaledListPrice(): Big | null {
    return PriceService.findUnitPrice(this.product.unit, this.listPrice);
  }

  get scaledListPriceString(): string {
    return PriceFormatter.formatPrice(this.scaledListPrice);
  }

  get effectiveCartPositionPrice(): Big | null {
    return PriceService.findEffectiveCartItemPrice(this.localItem);
  }

  get effectiveCartPositionPriceString(): string {
    return PriceFormatter.formatPrice(this.effectiveCartPositionPrice);
  }

  get amount(): number {
    return this.localItem.amount;
  }

  get offerPercentage(): Big | undefined {
    if (!this.hasOfferPrice) {
      return new Big(0);
    }
    return this.scaledListPrice?.minus(this.scaledEffectivePrice!).div(this.scaledListPrice!).mul(new Big(100)).round();
  }

  get depositString(): string {
    if (this.product.product.deposit) {
      return PriceFormatter.formatPrice(this.product.product.deposit);
    }
    return '';
  }

  get basePriceString(): string {
    const basePriceValue = PriceService.findBasePrice(this.product.product, this.product.unit);
    const referenceUnitString = this.product.product.basePrice && this.product.product.basePrice.refUnit;
    if (basePriceValue !== null && referenceUnitString) {
      return PriceFormatter.formatBasePrice(basePriceValue, referenceUnitString);
    } else {
      return '';
    }
  }

  get netFillString(): string {
    return this.product.unit.unitSize || '';
  }

  public get iconProperties(): ProductPropertyDto[] {
    if (this.product.product.properties) {
      return this.product.product.properties.filter(
        (x) => x !== this.properties.NEW && x !== this.properties.DAUERTIEFPREIS,
      );
    }
    return [];
  }

  public hasProperty(property: ProductPropertyDto): boolean {
    return (this.product.product.properties || []).includes(property);
  }

  get isNew() {
    return this.hasProperty(this.properties.NEW);
  }

  get isLowPrice() {
    return this.hasProperty(this.properties.DAUERTIEFPREIS);
  }

  get isEssential(): boolean {
    return !!this.product.product.essentialUnits?.length;
  }

  get isDefaultUnit(): boolean {
    return this.product.unit.abbreviation === this.product.product.units.defaultUnit;
  }

  get hasMultipleVisibleUnits(): boolean {
    return this.product.product.units.units.filter((unit) => unit.isVisible).length > 1;
  }

  get isActive(): boolean {
    return this.product.product.status === ProductStatusDto.ACTIVE;
  }

  public getBadgeIconPath(productProperty: ProductPropertyDto) {
    try {
      return require(`@/assets/badges/${productProperty.toLowerCase()}.svg`);
    } catch (error) {
      return '';
    }
  }
}
